// conectagran/frontend/src/pages/AdManager/GamChart.tsx
import { useEffect, useRef, useState, useMemo } from 'react'
import {
  createChart, CrosshairMode, CandlestickSeries, createSeriesMarkers,
} from 'lightweight-charts'
import type { GamChartPoint } from '@/api/hooks/useGam'

interface Props {
  data: GamChartPoint[]
}

// ─── Fibonacci ────────────────────────────────────────────────────────────────
const FIB_LEVELS = [
  { ratio: 0,     label: '0%',    color: '#475569' },
  { ratio: 0.236, label: '23.6%', color: '#60a5fa' },
  { ratio: 0.382, label: '38.2%', color: '#34d399' },
  { ratio: 0.5,   label: '50%',   color: '#c9a84c' },
  { ratio: 0.618, label: '61.8%', color: '#f97316' },
  { ratio: 0.786, label: '78.6%', color: '#f87171' },
  { ratio: 1,     label: '100%',  color: '#475569' },
]

// ─── Timeframes ───────────────────────────────────────────────────────────────
interface TfOption { label: string; secs: number }

const ALL_TFS: TfOption[] = [
  { label: '1h',  secs: 3600 },
  { label: '3h',  secs: 3600 * 3 },
  { label: '12h', secs: 3600 * 12 },
  { label: '1d',  secs: 86400 },
  { label: '7d',  secs: 86400 * 7 },
]

function getAvailableTfs(data: GamChartPoint[]): TfOption[] {
  if (data.length < 2) return [ALL_TFS[0]]
  const range = data[data.length - 1].time - data[0].time // seconds
  if (range <= 2 * 86400)  return ALL_TFS.slice(0, 2)   // 1h, 4h
  if (range <= 8 * 86400)  return ALL_TFS.slice(1, 3)   // 4h, 12h
  if (range <= 60 * 86400) return ALL_TFS.slice(2, 4)   // 12h, 1d
  return ALL_TFS.slice(3, 5)                             // 1d, 7d
}

// ─── Candle builder ───────────────────────────────────────────────────────────
interface Candle {
  time: number
  open: number
  high: number
  low: number
  close: number
  impressions: number
}

function buildCandles(data: GamChartPoint[], secs: number): Candle[] {
  if (data.length === 0) return []

  const map = new Map<number, GamChartPoint[]>()
  data.forEach(p => {
    const t = Math.floor(p.time / secs) * secs
    const arr = map.get(t) ?? []
    arr.push(p)
    map.set(t, arr)
  })

  const sorted = Array.from(map.entries()).sort(([a], [b]) => a - b)
  const candles: Candle[] = []

  sorted.forEach(([time, pts]) => {
    const ecpms  = pts.map(p => p.ecpm)
    // open = close of previous candle (standard convention); first candle uses own first ecpm
    const prevClose = candles.length > 0 ? candles[candles.length - 1].close : ecpms[0]
    const close  = ecpms[ecpms.length - 1]
    candles.push({
      time,
      open:  prevClose,
      high:  Math.max(prevClose, close, ...ecpms),
      low:   Math.min(prevClose, close, ...ecpms),
      close,
      impressions: pts.reduce((s, p) => s + p.impressions, 0),
    })
  })

  return candles
}

// ─── Significant-move detector ────────────────────────────────────────────────
// Compares close-to-close between adjacent candles. Returns the top-N rises
// and top-N drops (by absolute %) for annotation.
const MOVE_THRESHOLD = 0.08 // 8% minimum
const MAX_PER_SIDE   = 3    // top 3 rises + top 3 drops

function detectMoves(candles: Candle[]): any[] {
  if (candles.length < 2) return []

  const moves = candles.slice(1).map((c, i) => {
    const prev = candles[i]
    const pct  = prev.close > 0 ? (c.close - prev.close) / prev.close : 0
    return { time: c.time, pct }
  }).filter(m => Math.abs(m.pct) >= MOVE_THRESHOLD)

  const ups   = [...moves].filter(m => m.pct > 0)
    .sort((a, b) => b.pct - a.pct)
    .slice(0, MAX_PER_SIDE)

  const downs = [...moves].filter(m => m.pct < 0)
    .sort((a, b) => a.pct - b.pct)
    .slice(0, MAX_PER_SIDE)

  return [
    ...ups.map(m => ({
      time:     m.time as any,
      position: 'belowBar' as const,
      color:    '#4ade80',
      shape:    'arrowUp' as const,
      text:     `+${(m.pct * 100).toFixed(1)}%`,
      size:     1,
    })),
    ...downs.map(m => ({
      time:     m.time as any,
      position: 'aboveBar' as const,
      color:    '#f87171',
      shape:    'arrowDown' as const,
      text:     `${(m.pct * 100).toFixed(1)}%`,
      size:     1,
    })),
  ].sort((a, b) => (a.time as number) - (b.time as number))
}

// ─── Component ────────────────────────────────────────────────────────────────
export function GamChart({ data }: Props) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef     = useRef<ReturnType<typeof createChart> | null>(null)
  const seriesRef    = useRef<any>(null)
  const markersRef   = useRef<any>(null)
  const fibLinesRef  = useRef<any[]>([])

  const [showFib, setShowFib] = useState(false)

  const availableTfs = useMemo(() => getAvailableTfs(data), [data])
  const [tfLabel, setTfLabel] = useState<string>(availableTfs[0].label)

  // When period changes, available TFs change — reset to first available if needed
  useEffect(() => {
    if (!availableTfs.find(t => t.label === tfLabel)) {
      setTfLabel(availableTfs[0].label)
    }
  }, [availableTfs]) // eslint-disable-line react-hooks/exhaustive-deps

  const currentTf = availableTfs.find(t => t.label === tfLabel) ?? availableTfs[0]
  const candles   = useMemo(() => buildCandles(data, currentTf.secs), [data, currentTf])

  // ── Init chart (once) ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return

    const chart = createChart(containerRef.current, {
      layout: {
        background: { color: '#0d0f16' },
        textColor: '#94a3b8',
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: '#1a1d2a' },
        horzLines: { color: '#1a1d2a' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#252836' },
      timeScale: { borderColor: '#252836', timeVisible: true },
      width: containerRef.current.clientWidth,
      height: 320,
    })

    const series = chart.addSeries(CandlestickSeries, {
      upColor:       '#22c55e',
      downColor:     '#ef4444',
      borderVisible: false,
      wickUpColor:   '#22c55e',
      wickDownColor: '#ef4444',
      priceFormat:   { type: 'price', precision: 2, minMove: 0.01 },
    })

    const markers = createSeriesMarkers(series, [])

    chartRef.current   = chart
    seriesRef.current  = series
    markersRef.current = markers

    const ro = new ResizeObserver(() => {
      if (containerRef.current) {
        chart.applyOptions({ width: containerRef.current.clientWidth })
      }
    })
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current    = null
      seriesRef.current   = null
      markersRef.current  = null
      fibLinesRef.current = []
    }
  }, [])

  // ── Fibonacci helpers ──────────────────────────────────────────────────────
  function clearFibLines() {
    fibLinesRef.current.forEach(line => {
      try { seriesRef.current?.removePriceLine(line) } catch {}
    })
    fibLinesRef.current = []
  }

  function drawFibLines(high: number, low: number) {
    if (!seriesRef.current) return
    clearFibLines()
    const range = high - low
    FIB_LEVELS.forEach(({ ratio, label, color }) => {
      const price = high - range * ratio
      const line = seriesRef.current.createPriceLine({
        price,
        color,
        lineWidth: 1,
        lineStyle: 2,
        axisLabelVisible: true,
        title: `Fib ${label} $${price.toFixed(2)}`,
      })
      fibLinesRef.current.push(line)
    })
  }

  // ── Update data ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!seriesRef.current || !chartRef.current || candles.length === 0) return

    seriesRef.current.setData(candles.map(c => ({
      time:  c.time as any,
      open:  c.open,
      high:  c.high,
      low:   c.low,
      close: c.close,
    })))

    markersRef.current.setMarkers(detectMoves(candles))

    // Fibonacci — use candle high/low filtered by impressions
    const qualified = candles.filter(c => c.impressions > 100)
    const fibHigh   = Math.max(...candles.map(c => c.high))
    const fibLow    = qualified.length > 0
      ? Math.min(...qualified.map(c => c.low))
      : Math.min(...candles.map(c => c.low))

    if (showFib && fibHigh > fibLow) {
      drawFibLines(fibHigh, fibLow)
    } else {
      clearFibLines()
    }

    chartRef.current.timeScale().fitContent()
    const visibleRange = chartRef.current.timeScale().getVisibleLogicalRange()
    if (visibleRange) {
      const barsInView     = visibleRange.to - visibleRange.from
      const containerWidth = containerRef.current?.clientWidth ?? 800
      const spacing        = containerWidth / Math.max(barsInView, 1)
      if (spacing > 16) {
        chartRef.current.timeScale().applyOptions({ barSpacing: 16 })
      }
    }
  }, [candles, showFib])

  // ── Fib legend values (memo to avoid recalculating in render) ──────────────
  const fibValues = useMemo(() => {
    if (!showFib || candles.length === 0) return []
    const qualified = candles.filter(c => c.impressions > 100)
    const high = Math.max(...candles.map(c => c.high))
    const low  = qualified.length > 0
      ? Math.min(...qualified.map(c => c.low))
      : Math.min(...candles.map(c => c.low))
    return FIB_LEVELS.map(({ label, color, ratio }) => ({
      label, color, price: high - (high - low) * ratio,
    }))
  }, [showFib, candles])

  return (
    <div className="w-full">
      {/* ── Controls row ──────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between mb-2">
        {/* Timeframe selector */}
        <div className="flex gap-0.5 bg-[#1a1d2a] rounded p-0.5">
          {availableTfs.map(tf => (
            <button
              key={tf.label}
              onClick={() => setTfLabel(tf.label)}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold transition-colors ${
                currentTf.label === tf.label
                  ? 'bg-[#c9a84c] text-black'
                  : 'text-[#64748b] hover:text-[#94a3b8]'
              }`}
            >
              {tf.label}
            </button>
          ))}
        </div>

        {/* Fibonacci toggle */}
        <button
          onClick={() => setShowFib(v => !v)}
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-[11px] font-semibold border transition-colors ${
            showFib
              ? 'border-[#c9a84c] text-[#c9a84c] bg-[#c9a84c15]'
              : 'border-[#252836] text-[#475569] bg-[#1a1d2a] hover:text-[#94a3b8] hover:border-[#3a3d4e]'
          }`}
        >
          φ Fibonacci
        </button>
      </div>

      {/* ── Chart ─────────────────────────────────────────────────────────── */}
      <div className="relative">
        <div ref={containerRef} className="w-full" style={{ height: 320 }} />

        {data.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-[#475569] text-sm">
            Sem dados para o período selecionado
          </div>
        )}
      </div>

      {/* ── Legend row ────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-5 mt-2 flex-wrap">
        <span className="flex items-center gap-1 text-[10px] text-[#22c55e]">
          <span className="w-2 h-3 inline-block rounded-sm bg-[#22c55e]" />
          Alta
        </span>
        <span className="flex items-center gap-1 text-[10px] text-[#ef4444]">
          <span className="w-2 h-3 inline-block rounded-sm bg-[#ef4444]" />
          Queda
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-[#4ade80]">
          ▲ Alta ≥8%
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-[#f87171]">
          ▼ Queda ≥8%
        </span>
      </div>

      {/* ── Fibonacci legend ──────────────────────────────────────────────── */}
      {showFib && fibValues.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 pt-2 border-t border-[#1a1d2a]">
          {fibValues.map(({ label, color, price }) => (
            <span key={label} className="flex items-center gap-1 text-[10px]">
              <span
                className="w-3 h-px inline-block"
                style={{ backgroundColor: color, display: 'inline-block', verticalAlign: 'middle' }}
              />
              <span style={{ color }}>{label}</span>
              <span className="text-[#475569]">${price.toFixed(2)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
